import { useContext, useState, useMemo, useCallback } from "react";
import { AlquileresContext } from "../../../../context/AlquileresContext";
import { ActividadContext } from "../../../../context/ActividadContext";
import { saveLocatario, updateLocatario, deleteLocatario } from "../../../../application/locatarios";
import { FaPlusCircle, FaEdit, FaTrash, FaSearch, FaTimes } from "react-icons/fa";
import { useSortableData } from "../../../../hooks/useSortableData";
import SortableHeader from "../../../../components/SortableHeader";
import { nombreCompleto } from "../../../../utils/nombre";

function LocatarioList() {
  const { locatarios, contratos, refreshLocatarios, refreshContratos } = useContext(AlquileresContext);
  const { registrarActividad } = useContext(ActividadContext);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ apellido: "", nombre: "", telefono: "", telefono2: "", email: "", dni: "", observaciones: "" });
  const [saving, setSaving] = useState(false);

  const filtered = useMemo(() => {
    return locatarios.filter(
      (l) =>
        !search ||
        nombreCompleto(l).toLowerCase().includes(search.toLowerCase()) ||
        l.telefono?.includes(search) ||
        l.telefono2?.includes(search) ||
        l.dni?.includes(search) ||
        l.observaciones?.toLowerCase().includes(search.toLowerCase())
    );
  }, [locatarios, search]);

  const contratosCount = useCallback(
    (locatarioId) => contratos.filter((c) => c.locatarioId === locatarioId).length,
    [contratos]
  );

  const getValue = useCallback(
    (l, key) => {
      if (key === "contratos") return contratosCount(l.idFirebase);
      if (key === "nombre") return nombreCompleto(l);
      return l?.[key];
    },
    [contratosCount]
  );
  const { sortedData, sortConfig, requestSort } = useSortableData(filtered, {
    getValue,
    storageKey: "sort-locatarios",
    initial: { key: "nombre", direction: "asc" },
  });

  const openCreate = () => {
    setForm({ apellido: "", nombre: "", telefono: "", telefono2: "", email: "", dni: "", observaciones: "" });
    setModal({ mode: "create" });
  };

  const openEdit = (loc) => {
    setForm({
      apellido: loc.apellido || "",
      nombre: loc.nombre || "",
      telefono: loc.telefono || "",
      telefono2: loc.telefono2 || "",
      email: loc.email || "",
      dni: loc.dni || "",
      observaciones: loc.observaciones || "",
    });
    setModal({ mode: "edit", data: loc });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim() || !form.apellido.trim()) return;
    setSaving(true);
    try {
      if (modal.mode === "create") {
        await saveLocatario(form);
        registrarActividad("LOCATARIO_CREADO", {
          mensaje: `Locatario creado: ${nombreCompleto(form)}`,
          entidadTipo: "LOCATARIO",
          entidadLabel: nombreCompleto(form),
        });
      } else {
        await updateLocatario(modal.data.idFirebase, form);
        // the rename cascades onto contratos, so refresh them too
        await refreshContratos();
      }
      await refreshLocatarios();
      setModal(null);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este locatario?")) return;
    const locatario = locatarios.find((l) => l.idFirebase === id);
    try {
      await deleteLocatario(id);
      await refreshLocatarios();
      if (locatario) {
        registrarActividad("LOCATARIO_ELIMINADO", {
          mensaje: `Locatario eliminado: ${nombreCompleto(locatario)}`,
          entidadTipo: "LOCATARIO",
          entidadLabel: nombreCompleto(locatario),
        });
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  return (
    <div className="px-4 md:px-6 py-4 space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Locatarios</h1>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 bg-primary hover:bg-secondary text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
        >
          <FaPlusCircle className="w-4 h-4" /> Nuevo Locatario
        </button>
      </div>

      <div className="relative max-w-md">
        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
        <input
          type="text"
          placeholder="Buscar por nombre, telefono, DNI u observaciones..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent"
        />
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <SortableHeader sortKey="nombre" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="left" className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-400">Apellido y Nombre</SortableHeader>
              <SortableHeader sortKey="dni" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="left" className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-400">DNI</SortableHeader>
              <SortableHeader sortKey="telefono" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="left" className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-400">Telefono</SortableHeader>
              <SortableHeader sortKey="telefono2" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="left" className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-400">Telefono 2</SortableHeader>
              <SortableHeader sortKey="email" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="left" className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-400">Email</SortableHeader>
              <SortableHeader sortKey="observaciones" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="left" className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-400">Observaciones</SortableHeader>
              <SortableHeader sortKey="contratos" currentKey={sortConfig.key} direction={sortConfig.direction} onSort={requestSort} align="center" className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-400">Contratos</SortableHeader>
              <th className="text-center px-4 py-3 font-semibold text-gray-600 dark:text-gray-400">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {sortedData.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-gray-400">
                  No se encontraron locatarios
                </td>
              </tr>
            ) : (
              sortedData.map((l) => (
                <tr key={l.idFirebase} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                  <td className="px-4 py-3 font-medium text-gray-800 dark:text-gray-200">{nombreCompleto(l)}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{l.dni || "-"}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400 whitespace-nowrap">{l.telefono || "-"}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400 whitespace-nowrap">{l.telefono2 || "-"}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{l.email || "-"}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400 max-w-xs truncate" title={l.observaciones || ""}>
                    {l.observaciones || "-"}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                      {contratosCount(l.idFirebase)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={() => openEdit(l)} className="text-gray-400 hover:text-primary transition-colors">
                        <FaEdit className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(l.idFirebase)} className="text-gray-400 hover:text-red-500 transition-colors">
                        <FaTrash className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-xl mx-4 p-8">
            <div className="flex items-center justify-between pb-4 mb-5 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100">
                {modal.mode === "create" ? "Nuevo Locatario" : "Editar Locatario"}
              </h3>
              <button onClick={() => setModal(null)} className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nombre *</label>
                  <input type="text" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Apellido *</label>
                  <input type="text" value={form.apellido} onChange={(e) => setForm({ ...form, apellido: e.target.value })} className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent" required />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">DNI</label>
                <input type="text" value={form.dni} onChange={(e) => setForm({ ...form, dni: e.target.value })} className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Telefono</label>
                  <input type="text" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Telefono 2</label>
                  <input type="text" value={form.telefono2} onChange={(e) => setForm({ ...form, telefono2: e.target.value })} className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Observaciones</label>
                <textarea value={form.observaciones} onChange={(e) => setForm({ ...form, observaciones: e.target.value })} rows={2} className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent resize-none" placeholder="Notas internas, alias, etc." />
              </div>
              <div className="flex justify-end gap-3 pt-4 mt-2 border-t border-gray-100 dark:border-gray-700">
                <button type="button" onClick={() => setModal(null)} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">Cancelar</button>
                <button type="submit" disabled={saving} className="px-4 py-2 text-sm bg-primary hover:bg-secondary text-white rounded-lg disabled:opacity-50">{saving ? "Guardando..." : "Guardar"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default LocatarioList;
